
import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { useQuery } from "@tanstack/react-query"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"

type FormData = {
  first_name: string
  last_name: string
  state: string
  city: string
  role: string
  new_password?: string
  prefeito?: string
}

export function useUserForm(userId: string | null, onSuccess: () => void, onOpenChange: (open: boolean) => void) {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [selectedState, setSelectedState] = useState("")
  const form = useForm<FormData>()

  const { data: states } = useQuery({
    queryKey: ["states"],
    queryFn: async () => {
      const response = await fetch(
        "https://servicodados.ibge.gov.br/api/v1/localidades/estados"
      )
      const data = await response.json()
      return data.sort((a: any, b: any) => a.nome.localeCompare(b.nome))
    },
  })

  const { data: cities } = useQuery({
    queryKey: ["cities", selectedState],
    queryFn: async () => {
      if (!selectedState) return []
      const state = states?.find((s: any) => s.sigla === selectedState)
      if (!state) return []

      const response = await fetch(
        `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${state.id}/municipios`
      )
      const data = await response.json()
      return data.sort((a: any, b: any) => a.nome.localeCompare(b.nome))
    },
    enabled: !!selectedState && !!states,
  })

  const { data: mayors } = useQuery({
    queryKey: ["mayors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("municipalities")
        .select("mayor_name, name")
        .order("name")
      
      if (error) {
        console.error("Erro ao carregar prefeitos:", error)
        return []
      }
      
      return data || []
    },
  })

  const { data: profile, isError } = useQuery({
    queryKey: ["user", userId],
    queryFn: async () => {
      if (!userId) return null
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .maybeSingle()

      if (error) {
        toast({
          variant: "destructive",
          title: "Erro ao carregar usuário",
          description: "Por favor tente novamente.",
        })
        onOpenChange(false)
        throw error
      }
      
      if (!data) {
        toast({
          variant: "destructive",
          title: "Usuário não encontrado",
          description: "O perfil solicitado não existe ou foi removido.",
        })
        onOpenChange(false)
        return null
      }

      return data
    },
    enabled: !!userId,
  })

  useEffect(() => {
    if (profile) {
      setSelectedState(profile.state || "")
    }
  }, [profile])

  useEffect(() => {
    if (profile && (!profile.city || cities)) {
      form.reset({
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        state: profile.state || "",
        city: profile.city || "",
        role: profile.role || "vereador",
        new_password: "",
        prefeito: profile.prefeito || "",
      })
    }
  }, [profile, cities, form])

  const onSubmit = async (data: FormData) => {
    if (!userId) return
    
    setLoading(true)
    try {
      if (data.new_password) {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) throw new Error('Não autorizado')

        const response = await fetch(
          'https://iaddwpyaxyaayklwogux.supabase.co/functions/v1/update-user-password',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              userId,
              password: data.new_password,
            }),
          }
        )

        if (!response.ok) {
          const error = await response.json()
          throw new Error(error.error || 'Erro ao atualizar senha')
        }
      }

      const { error } = await supabase
        .from("profiles")
        .update({
          first_name: data.first_name,
          last_name: data.last_name,
          state: data.state,
          city: data.city,
          role: data.role,
          prefeito: data.prefeito,
        })
        .eq("id", userId)

      if (error) throw error

      toast({
        title: "Usuário atualizado com sucesso",
        description: data.new_password ? "Senha alterada com sucesso." : undefined,
      })
      onSuccess()
      onOpenChange(false)
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error)
      toast({
        variant: "destructive",
        title: "Erro ao atualizar usuário",
        description: error.message || "Por favor tente novamente.",
      })
    }
    setLoading(false)
  }

  return {
    form,
    loading,
    onSubmit,
    profile,
    states,
    cities,
    mayors,
    selectedState,
    setSelectedState,
    isError
  }
}
